import { useMemo } from "react";
import StarIcon from "@mui/icons-material/Star";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { formatDelta } from "./insights/insightsFormat";
import { deltaClass, soft } from "./insights/softClasses";
import { memberDelta, memberRate } from "../../../lib/progressCompare";
import UserAvatar from "../../../components/ui/UserAvatar";

const SORTERS = {
  completionRate: memberRate,
  daysActiveCount: (r) => r.daysActiveCount || 0,
  perfectDaysCount: (r) => r.perfectDaysCount || 0,
  member: (r) => `${r.name || ""} ${r.surname || ""}`.toLowerCase(),
};

/**
 * Üye ilerlemeleri: tam genişlik, çizgiyle ayrılmış tek katmanlı satırlar
 * (baş harf, ad, ince çubuk, oran; altında aktif gün, mükemmel gün, toplam).
 * Oran periodRate (yoksa completionRate), fark sunucunun delta'sı; eski
 * sunucuda previousById (actorId → önceki eşit dönemdeki oran) ile hesaplanır.
 */
export default function MemberProgressList({
  userStats,
  sortKey,
  selectedId,
  onSelect,
  compact,
  previousById,
  compareText,
}) {
  const { t } = useScriptMode();

  const sorted = useMemo(() => {
    const list = Array.isArray(userStats) ? userStats.slice() : [];
    const fn = SORTERS[sortKey] || SORTERS.completionRate;
    // İsimde artan, sayısal alanlarda azalan sıralama daha doğal.
    const asc = sortKey === "member";
    list.sort((a, b) => {
      const va = fn(a);
      const vb = fn(b);
      if (va !== vb) {
        if (va < vb) return asc ? -1 : 1;
        return asc ? 1 : -1;
      }
      // Eşitlikte (ör. herkesin tam günü 0 ise) tamamlanma yüzdesi belirleyici;
      // o da eşitse isim sırası.
      const ca = SORTERS.completionRate(a);
      const cb = SORTERS.completionRate(b);
      if (ca !== cb) return cb - ca;
      return SORTERS.member(a).localeCompare(SORTERS.member(b), "tr");
    });
    return list;
  }, [userStats, sortKey]);

  if (sorted.length === 0) {
    return <p className={`text-sm ${soft.muted}`}>{t("manage_stats_no_data")}</p>;
  }

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0">
      <ul className="tk-stagger flex flex-col lg:flex-1 lg:min-h-0 lg:overflow-y-auto border-t border-terakkiBorder dark:border-white/15">
        {sorted.map((row) => {
          const percent = memberRate(row);
          const selected = String(row.actorId) === String(selectedId);
          const prev = previousById ? previousById.get(String(row.actorId)) : undefined;
          // Sunucu farkı önce; eski sunucuda önceki dönemin oranından.
          const delta = memberDelta(row, prev);
          return (
            <li key={row.actorId}>
              <button
                type="button"
                data-member={row.actorId}
                aria-pressed={selected}
                onClick={() => onSelect && onSelect(String(row.actorId))}
                className={[
                  "w-full text-left flex items-center gap-3 px-3 py-3 border-b border-terakkiBorder transition-colors dark:border-white/15",
                  selected
                    ? "bg-terakkiBrandCloud dark:bg-white/[0.08]"
                    : "hover:bg-terakkiBrandCloud/60 dark:hover:bg-white/5",
                ].join(" ")}
              >
                <UserAvatar person={row} size={compact ? 32 : 36} />
                <span className="min-w-0 flex-1 flex flex-col gap-1">
                  <span className={`text-[15px] font-medium truncate ${soft.ink}`}>
                    {row.name} {row.surname}
                  </span>
                  <span className={soft.track}>
                    <span data-bar className={`block ${soft.fill}`} style={{ width: `${percent}%` }} />
                  </span>
                  {/* Liste daraldığında alt bilgi gizlenir; detay sağdaki panelde. */}
                  {!compact && (
                    <span className={`flex items-center gap-2.5 text-[11px] ${soft.faint}`}>
                      <span className="inline-flex items-center gap-1">
                        <EventAvailableIcon sx={{ fontSize: 13 }} aria-hidden />
                        {row.daysActiveCount || 0} {t("manage_stats_table_active_days")}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <StarIcon sx={{ fontSize: 13 }} aria-hidden />
                        {row.perfectDaysCount || 0} {t("manage_stats_table_perfect_days")}
                      </span>
                      <span>
                        {row.totalValue || 0} / {row.totalGoalValue || 0}
                      </span>
                    </span>
                  )}
                </span>
                <span className="flex flex-col items-end shrink-0 min-w-[48px]">
                  <span className={`font-medium ${soft.accent} ${compact ? "text-sm" : "text-base"}`}>
                    %{percent}
                  </span>
                  {delta !== null ? (
                    <span className={`text-[11px] ${deltaClass(delta)}`} title={compareText}>
                      {formatDelta(delta)}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
